import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to track an element's size using ResizeObserver
 * Returns a ref to attach to the element and its current dimensions
 *
 * @returns {Object} Object containing elementRef and size {width, height}
 *
 * @example
 * const { elementRef, size } = useElementSize();
 *
 * <div ref={elementRef}>
 *   Size: {size.width}x{size.height}
 * </div>
 */
export const useElementSize = () => {
  const elementRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      if (elementRef.current) {
        setSize({
          width: elementRef.current.offsetWidth,
          height: elementRef.current.offsetHeight,
        });
      }
    };

    // Set initial size
    updateSize();

    // Create ResizeObserver to track element size changes
    const resizeObserver = new ResizeObserver(updateSize);
    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  return { elementRef, size };
};
